import {
    NetworkError,
    generateRouteDefinition,
    makeCanonicalUrl,
} from '@jet-app/music-classical/utilities';
import {
    makeBrowsePlaylistsPageIntent,
    type BrowsePlaylistsPageIntent,
} from './BrowsePlaylistsPageIntent';
import type { IntentController } from '@jet/environment/dispatching';
import type { RouteProvider } from '@jet/environment/routing';
import type { ObjectGraph } from '@jet/environment/dependencies';
import type { Page } from '@jet-app/music-classical/models';
import { IntentKind } from '../../../common';
import { renderBrowseDetailPage } from '../renderBrowseDetailPage';
import { requestEntitiesList } from '@jet-app/music-classical/api/request-entities-list';
import type { ClassicalListViewPage } from '@jet-app/music-classical/api/types';
import { makeBrowseDetailPaginationIntent } from '../paginate/BrowseDetailPaginationIntent';
import { pageSortByChangeEventTrigger } from '@jet-app/music-classical/intents/common/page-invalidation-rules';
import { requestViewPageWithQuery } from '@jet-app/music-classical/api/request-view-page';
import type { SortParams } from '@jet-app/music-classical/intents/browse-page/types';

const canonicalUrlRule = {
    path: '/{storefront}/browse/playlists/{id}',
    query: [],
};

const defaultParams: SortParams = {
    sortBy: 'playlist.sortingTitle.keyword',
    sortOrder: 'ascending',
};

export const BrowsePlaylistsPageIntentController: IntentController<BrowsePlaylistsPageIntent> &
    RouteProvider = {
    $intentKind: IntentKind.BROWSE_PLAYLISTS_PAGE,

    routes: generateRouteDefinition<BrowsePlaylistsPageIntent>(
        canonicalUrlRule,
        makeBrowsePlaylistsPageIntent,
    ),

    async perform(
        intent: BrowsePlaylistsPageIntent,
        objectGraph: ObjectGraph,
    ): Promise<Page> {
        const canonicalURL = makeCanonicalUrl(
            canonicalUrlRule,
            intent,
            objectGraph,
        );

        const { entityPath, metadata } = intent;
        let apiResponse;

        if (metadata?.url) {
            apiResponse = await requestViewPageWithQuery(
                intent.metadata.url,
                objectGraph,
            );
        } else if (entityPath) {
            apiResponse = await requestEntitiesList(
                entityPath,
                objectGraph,
                defaultParams,
            );
        } else {
            apiResponse = await requestEntitiesList(
                `editorial-playlist-container/${intent.id}`,
                objectGraph,
                defaultParams,
            );
        }

        if (!apiResponse?.body) {
            throw NetworkError.noContent();
        }

        const nextPageURL = (apiResponse.body as ClassicalListViewPage)
            .firstPage?.next;

        const page = renderBrowseDetailPage(
            canonicalURL,
            apiResponse,
            'playlists',
            objectGraph,
        );

        return {
            ...page,
            nextIntent: nextPageURL
                ? makeBrowseDetailPaginationIntent({
                      url: nextPageURL,
                      id: page?.sections?.[0]?.id,
                  })
                : null,
            invalidationRules: {
                eventTriggers: [pageSortByChangeEventTrigger(intent)],
            },
        };
    },
};
